/**
 * useRegistration Hook
 * 라이더 회원가입 관련 로직을 처리하는 커스텀 훅
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    requestRegistrationEmail,
    verifyRegistrationToken,
    completeRegistration,
} from '../api/registrationApi';
import { ParsedError } from '../../../core/api/errorParser';
import {
    RegistrationVerifyResponse,
    RegistrationCompleteResponse,
} from '../types/registration';
import { setCookie } from '../../../core/utils/cookie';
import { setUserInfo } from '../../../core/utils';

/**
 * 회원가입 이메일 요청 훅
 */
export const useRegistrationRequest = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [alreadyRegistered, setAlreadyRegistered] = useState(false);

    const handleRequestEmail = useCallback(
        async (riderId: string, email: string) => {
            setIsLoading(true);
            setError(null);
            setSuccess(false);
            setAlreadyRegistered(false);

            try {
                const response = await requestRegistrationEmail({ riderId, email });

                if (response.success) {
                    setSuccess(true);
                } else {
                    // 이미 회원가입된 사용자 체크
                    if (response.message === 'ALREADY_REGISTERED') {
                        setAlreadyRegistered(true);
                    } else {
                        setError(response.message || '이메일 발송에 실패했습니다');
                    }
                }
            } catch (err: unknown) {
                // ParsedError 형식 체크 (API 클라이언트가 변환한 에러)
                const parsedError = err as ParsedError;
                if (parsedError && parsedError.name === 'ApiError') {
                    const errorMessage = parsedError.message;
                    // 이미 회원가입된 사용자 체크
                    if (errorMessage === 'ALREADY_REGISTERED') {
                        setAlreadyRegistered(true);
                    } else {
                        setError(errorMessage || '이메일 발송 중 오류가 발생했습니다');
                    }
                } else {
                    setError('네트워크 오류가 발생했습니다');
                }
            } finally {
                setIsLoading(false);
            }
        },
        [navigate]
    );

    return {
        handleRequestEmail,
        isLoading,
        error,
        success,
        alreadyRegistered,
        setError,
        setSuccess,
        setAlreadyRegistered,
    };
};

/**
 * 토큰 검증 훅
 */
export const useRegistrationVerify = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [riderInfo, setRiderInfo] = useState<RegistrationVerifyResponse | null>(null);

    const handleVerifyToken = useCallback(async (token: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await verifyRegistrationToken({ token });

            if (response.success) {
                setRiderInfo(response);
            } else {
                setError(response.message || '토큰 검증에 실패했습니다');
            }
        } catch (err: unknown) {
            // ParsedError 형식 체크 (API 클라이언트가 변환한 에러)
            const parsedError = err as ParsedError;
            if (parsedError && parsedError.name === 'ApiError') {
                setError(parsedError.message || '토큰 검증 중 오류가 발생했습니다');
            } else {
                setError('네트워크 오류가 발생했습니다');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        handleVerifyToken,
        isLoading,
        error,
        riderInfo,
        setError,
    };
};

/**
 * 회원가입 완료 훅
 */
export const useRegistrationComplete = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleComplete = useCallback(
        async (
            token: string,
            password: string,
            confirmPassword: string,
            phoneNum: string,
            email: string
        ) => {
            setIsLoading(true);
            setError(null);

            try {
                // 오늘 날짜를 YYYYMMDD 형식으로 생성
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                const nextAcctApplyDay = `${year}${month}${day}`;

                const response: RegistrationCompleteResponse = await completeRegistration({
                    token,
                    password,
                    confirmPassword,
                    phoneNum,
                    email,
                    nextAcctApplyDay, // 오늘 날짜로 설정
                    nextAcctApplyType: 'P', // 선정산으로 기본 설정
                });

                if (response.success) {
                    // 토큰 저장
                    if (response.accessToken) {
                        setCookie('accessToken', response.accessToken, { expires: 7 });
                    }
                    if (response.refreshToken) {
                        setCookie('refreshToken', response.refreshToken, { expires: 30 });
                    }

                    // 사용자 정보 저장 (기본값으로 설정)
                    setUserInfo({
                        userName: response.userName || '',
                    });

                    // 홈으로 이동
                    navigate('/home', { replace: true });
                } else {
                    setError(response.message || '회원가입에 실패했습니다');
                }
            } catch (err: unknown) {
                // ParsedError 형식 체크 (API 클라이언트가 변환한 에러)
                const parsedError = err as ParsedError;
                if (parsedError && parsedError.name === 'ApiError') {
                    setError(parsedError.message || '회원가입 중 오류가 발생했습니다');
                } else {
                    setError('네트워크 오류가 발생했습니다');
                }
            } finally {
                setIsLoading(false);
            }
        },
        [navigate]
    );

    return {
        handleComplete,
        isLoading,
        error,
        setError,
    };
};
